/** INSTALAȚIA ELECTRICĂ: aparatele, circuitele și DRUMUL PE CARE MERGE FIRUL.
 *
 *  ==========================================================================
 *  PARTEA CARE CONTEAZĂ E TRASEUL, nu simbolurile.
 *
 *  O planșă de electrice desenată ca în manual leagă întrerupătorul de corpul
 *  de iluminat printr-un arc, peste mijlocul camerei. Arcul ăla e o RELAȚIE —
 *  «ăsta comandă ăla» —, nu un drum: firul nu trece prin aer pe la doi metri
 *  deasupra canapelei. Bun pentru cine citește schema, inutil pentru cine dă în
 *  perete cu bormașina.
 *
 *  Aici se desenează DRUMUL. Un fir de instalație îngropată merge în trei
 *  bucăți, și toate trei se pot desena:
 *
 *    1. SE URCĂ sau se coboară de la aparat până la banda de pozare — un șanț
 *       vertical în tencuială, de la priză în sus sau de la întrerupător în sus;
 *    2. SE MERGE ORIZONTAL pe bandă, de-a lungul pereților. Instalația nu taie
 *       camera pe diagonală, fiindcă tavanul e placă de beton și podeaua e șapă
 *       cu termoizolație: firul stă în zidărie, deci urmează zidăria;
 *    3. SE COBOARĂ la tablou.
 *
 *  BANDA DE POZARE, cea de la punctul 2, e la o cotă care nu se alege din
 *  desen: circuitele de lumină se duc la 0,30 m SUB placă, fiindcă acolo ies
 *  spre plafoniere și spre întrerupătoare; cele de prize la 0,30 m DEASUPRA
 *  pardoselii, la cota prizelor. Între ele, zona în care se bate un cui e
 *  liberă — și exact asta trebuie să arate desenul.
 *
 *  DE CE MERGE PE AXELE PEREȚILOR ȘI NU PE CONTURUL CAMEREI. Fiindcă acolo
 *  merge și în realitate: șanțul se taie în zid, iar zidul are un ax. Graful de
 *  noduri al pereților îl are deja făcut editorul — `model::nodurile`, scris ca
 *  un perete tras de capăt să-și ducă vecinii — deci drumul firului e un
 *  DIJKSTRA pe același graf. Nu e o coincidență fericită: sunt aceleași ziduri.
 *
 *  CE NU FACE. Nu dimensionează: nu alege secțiuni, nu însumează puteri, nu
 *  spune câți amperi trece siguranța. Cifrele de mai jos (1,5 mm² / 10 A la
 *  lumină, 2,5 / 16 la prize) sunt cele obișnuite ale unei locuințe și se arată
 *  ca ETICHETE de circuit, nu ca rezultatul unui calcul. Un calcul de instalație
 *  se face pe puteri instalate și pe simultaneități, iar un editor de șabloane
 *  nu le are.
 *  ==========================================================================
 */
import type { Pt } from "../doc";
import { t } from "../../../i18n";
import type { Cheie } from "../../../i18n";

// ------------------------------------------------------------------ aparate ---

export type FelAparat = {
  kind: string;
  nume: string;
  /** Grupa din paletă. */
  grupa: string;
  /** Înălțimea de montaj implicită, de la pardoseala finită. `"placa"` =
   *  chiar înălțimea nivelului — o plafonieră nu are o cotă a ei, are cota
   *  tavanului, oricare ar fi el. */
  h: number | "placa";
  circuit: Circuit;
  /** Consumă (priză, corp de iluminat) sau comandă (întrerupător)? Din asta
   *  iese ce se leagă de ce: un întrerupător nu e capătul unui circuit, e o
   *  întrerupere pe drumul spre corpul pe care-l aprinde. */
  rol: "consumator" | "comanda" | "sursa";
  /** Cât de lat e simbolul pe planșă, în metri — ca două prize alăturate să nu
   *  se calce. */
  d: number;
  /** The modules the device occupies in a mounting-box frame (default 1): the
   *  box shows their sum, which is what the electrician cuts the frame for. */
  module?: number;
  /** One of the architect's symbols: it stands on the wall on its foot, in the
   *  room, instead of sitting on the face. */
  picior?: boolean;
  /** The symbol's drawn size where it differs from `d`, which stays the
   *  device's size for spacing and the 3D plate. */
  simbol?: number;
  /** The letters of the architect's boxed symbols (sheet rows 122–126): the
   *  device is drawn as a double frame holding them. */
  litere?: string;
  /** The box family a device of another group still joins: the TV and data
   *  outlets are weak currents on the plan but sit in a socket frame. */
  inDoza?: FamilieDoza;
  /** Its mounting height is written beside it on the plan (sheet rows 4.3,
   *  126). */
  cotaPePlan?: boolean;
  /** Rețeaua aparatului, când nu e cea electrică: un calorifer sau o gură de
   *  aer se pun tot din paleta modului, dar le leagă motorul de instalații
   *  (`mep/`), nu firul spre tablou. Lipsă = electric. */
  retea?: Retea;
};

export type Retea = "electric" | "incalzire" | "ventilatie";

export type Circuit = "lumina" | "prize" | "forta" | "date";

/** CIRCUITELE unei locuințe, cu eticheta lor.
 *
 *  Separarea luminii de prize nu e o convenție de desen: sunt două circuite
 *  fiindcă o siguranță sărită la prize nu are voie să te lase pe întuneric pe
 *  scară. Forța e separată fiindcă o mașină de spălat sau un cuptor trag singure
 *  cât tot restul camerei. Curenții slabi (TV, date) merg pe traseu propriu,
 *  la distanță de cei tari, altfel se induc în ei. */
export const CIRCUITE: Record<Circuit, {
  nume: string; sectiune: string; culoare: string; banda: "sus" | "jos";
}> = {
  lumina: { nume: "Iluminat", sectiune: "1,5 mm² · 10 A", culoare: "#e8a33d",
            banda: "sus" },
  prize:  { nume: "Prize", sectiune: "2,5 mm² · 16 A", culoare: "#3d7fe8",
            banda: "jos" },
  forta:  { nume: "Forță", sectiune: "4 mm² · 20 A", culoare: "#d2453b",
            banda: "jos" },
  date:   { nume: "Curenți slabi", sectiune: "TV · date", culoare: "#c8a200",
            banda: "jos" },
};

/** CÂT DE SUS SE POZEAZĂ, față de placă sau de pardoseală.
 *
 *  0,30 m în amândouă sensurile: e distanța obișnuită la care se taie șanțul
 *  orizontal, și e aleasă exact ca zona de mijloc a peretelui — acolo unde se
 *  prinde un dulap sau se bate un cui — să rămână fără fire. */
export const RETRAGERE_BANDA = 0.3;

/** Cota benzii de pozare a unui circuit, în metri de la pardoseală. */
export const cotaBenzii = (circuit: Circuit, nivel: number): number =>
  (CIRCUITE[circuit].banda === "sus" ? nivel - RETRAGERE_BANDA : RETRAGERE_BANDA);

/** APARATELE, cu înălțimile de montaj obișnuite ale unei locuințe.
 *
 *  De unde vin cifrele: din ghidul biroului («Ghid de amplasare a iluminatului
 *  și aparatajului electric — ediție extinsă», 5.10.2026), pe axul aparatului,
 *  de la pardoseala finită:
 *    · priza la 0,30 m — priza generală în cameră uscată (cap. 2);
 *    · priza de blat la 1,10 m — blat + 20: blatul are 0,90, iar priza stă
 *      deasupra lui, ca să nu intre sub aparate (cap. 2, 8);
 *    · întrerupătorul la 1,05 m — H 105 la intrare, pe partea clanței (cap. 2);
 *    · aplica la 1,60 m — banda livingului și a holului, 160–180 (cap. 5);
 *      dormitorul (135) și oglinda băii (150–165) se scriu pe aparat;
 *    · tabloul la 1,60 m — organele de acționare la îndemână, fără scară.
 *
 *  Plafoniera și spotul n-au cotă proprie: au cota PLĂCII. Un nivel mai înalt le
 *  urcă pe amândouă, fără să le atingă nimeni. */
export const APARATE: FelAparat[] = [
  { kind: "priza", nume: "Priză schuko", grupa: "Prize", h: 0.3,
    circuit: "prize", rol: "consumator", d: 0.16, module: 2, picior: true },
  { kind: "priza_simpla", nume: "Priză simplă", grupa: "Prize", h: 0.3,
    circuit: "prize", rol: "consumator", d: 0.16, module: 1, picior: true },
  { kind: "priza_dubla", nume: "Priză dublă", grupa: "Prize", h: 0.3,
    circuit: "prize", rol: "consumator", d: 0.24, module: 4 },
  { kind: "priza_blat", nume: "Priză peste blat", grupa: "Prize", h: 1.1,
    circuit: "prize", rol: "consumator", d: 0.16, module: 2 },
  { kind: "priza_forta", nume: "Priză de forță", grupa: "Prize", h: 0.3,
    circuit: "forta", rol: "consumator", d: 0.2, simbol: 0.16, module: 2,
    picior: true },
  { kind: "priza_date", nume: "Priză internet / date", grupa: "Curenți slabi",
    h: 0.3, circuit: "date", rol: "consumator", d: 0.16, simbol: 0.24, module: 1,
    picior: true, inDoza: "prize" },
  { kind: "priza_tv", nume: "Priză TV", grupa: "Curenți slabi", h: 0.3,
    circuit: "date", rol: "consumator", d: 0.16, simbol: 0.24, module: 1,
    picior: true, inDoza: "prize" },
  // The weak currents of the entrance (sheet rows 123–126), at the guide's
  // heights (p. 19): thermostat 1.40, intercom 1.50, the bell push beside it.
  { kind: "interfon", nume: "Interfon", grupa: "Curenți slabi", h: 1.5,
    circuit: "date", rol: "consumator", d: 0.2, picior: true, litere: "IN",
    cotaPePlan: true },
  { kind: "sonerie", nume: "Sonerie", grupa: "Curenți slabi", h: 1.5,
    circuit: "date", rol: "consumator", d: 0.2, picior: true, litere: "SO",
    cotaPePlan: true },
  { kind: "termostat", nume: "Termostat", grupa: "Curenți slabi", h: 1.4,
    circuit: "date", rol: "consumator", d: 0.2, picior: true, litere: "T",
    cotaPePlan: true },

  { kind: "intrerupator", nume: "Întrerupător simplu", grupa: "Comenzi", h: 1.05,
    circuit: "lumina", rol: "comanda", d: 0.16, module: 1, picior: true },
  { kind: "intrerupator_dublu", nume: "Întrerupător dublu", grupa: "Comenzi",
    h: 1.05, circuit: "lumina", rol: "comanda", d: 0.22, module: 2 },
  { kind: "comutator", nume: "Întrerupător cap scară / cruce", grupa: "Comenzi",
    h: 1.05, circuit: "lumina", rol: "comanda", d: 0.16, module: 1, picior: true },
  // The dwelling's «all off» (sheet row 119): it commands every light and
  // socket of the dwelling, so it carries no index.
  { kind: "intrerupator_general", nume: "Întrerupător general", grupa: "Comenzi",
    h: 1.05, circuit: "lumina", rol: "comanda", d: 0.16, module: 1, picior: true },

  { kind: "plafoniera", nume: "Plafonieră", grupa: "Iluminat", h: "placa",
    circuit: "lumina", rol: "consumator", d: 0.36 },
  { kind: "spot", nume: "Spot", grupa: "Iluminat", h: "placa",
    circuit: "lumina", rol: "consumator", d: 0.14 },
  { kind: "aplica", nume: "Aplică", grupa: "Iluminat", h: 1.6,
    circuit: "lumina", rol: "consumator", d: 0.2, simbol: 0.1, picior: true,
    cotaPePlan: true },
  // The LED strip (sheet row 114): a start on a wall face and a length along
  // it (`length_m`), under the slab unless a height is written.
  { kind: "banda_led", nume: "Bandă LED", grupa: "Iluminat", h: "placa",
    circuit: "lumina", rol: "consumator", d: 0.02 },

  { kind: "tablou", nume: "Tablou electric", grupa: "Tablou", h: 1.6,
    circuit: "prize", rol: "sursa", d: 0.4, litere: "TE" },

  // The mounting box: the sockets and switches in it are its `devices`, so its
  // own height and circuit are only what an empty box falls back to.
  { kind: "doza", nume: "Doză", grupa: "Doze", h: 0.3,
    circuit: "prize", rol: "consumator", d: 0.2 },

  // INSTALAȚIILE: aparatele pe care motorul de instalații (`mep/`) doar le
  // leagă — omul le alege locul, ca la prize. Cotele, pe axul racordului:
  // caloriferul la 0,15 m (racordurile de jos ale panoului, I 13 art. 6.23),
  // port-prosopul la 0,30 m, distribuitorul la 0,50 m în cutia lui. Marcajul de
  // încălzire în pardoseală spune doar CAMERA: bucla o trage motorul pe toată
  // podeaua ei. Gurile de aer stau în plafon; hota, la 0,65 m peste blat.
  { kind: "calorifer", nume: "Calorifer", grupa: "Încălzire", h: 0.15,
    circuit: "prize", rol: "consumator", d: 0.8, picior: true, retea: "incalzire" },
  { kind: "port_prosop", nume: "Port-prosop", grupa: "Încălzire", h: 0.3,
    circuit: "prize", rol: "consumator", d: 0.5, picior: true, retea: "incalzire" },
  { kind: "incalzire_pardoseala", nume: "Încălzire în pardoseală", grupa: "Încălzire",
    h: 0, circuit: "prize", rol: "consumator", d: 0.4, litere: "IP", retea: "incalzire" },
  { kind: "distribuitor_incalzire", nume: "Distribuitor de încălzire", grupa: "Încălzire",
    h: 0.5, circuit: "prize", rol: "consumator", d: 0.5, picior: true, litere: "DI",
    retea: "incalzire" },
  { kind: "grila_extractie", nume: "Gură de extracție", grupa: "Ventilație", h: "placa",
    circuit: "prize", rol: "consumator", d: 0.2, retea: "ventilatie" },
  { kind: "grila_introducere", nume: "Gură de introducere", grupa: "Ventilație",
    h: "placa", circuit: "prize", rol: "consumator", d: 0.2, retea: "ventilatie" },
  { kind: "hota", nume: "Hotă", grupa: "Ventilație", h: 1.55,
    circuit: "prize", rol: "consumator", d: 0.6, picior: true, litere: "H",
    retea: "ventilatie" },
  { kind: "unitate_ventilare", nume: "Unitate de ventilare", grupa: "Ventilație",
    h: "placa", circuit: "prize", rol: "consumator", d: 0.6, litere: "UV",
    retea: "ventilatie" },
];

const PE_KIND = new Map(APARATE.map((a) => [a.kind, a]));

/** NUMELE APARATULUI ȘI AL CIRCUITULUI, în limba ecranului.
 *
 *  Ca la mobilier (v. `arhMobilierPlan::numePiesa`): catalogul ține numele
 *  românesc fiindcă el e sursa tehnică, ecranul ia cheia `aparat.<kind>`. Un
 *  fel dintr-un document mai nou decât interfața păstrează numele scris. */
export const numeAparat = (kind: string): string =>
  (PE_KIND.has(kind) ? t(`aparat.${kind}` as Cheie) : kind);

export const numeCircuit = (circuit: string): string =>
  (circuit in CIRCUITE ? t(`circuit.${circuit}` as Cheie) : circuit);

export const aparatul = (kind: string): FelAparat | null =>
  PE_KIND.get(kind) ?? null;

/** Înălțimea de montaj a unui fel, cu placa rezolvată la nivelul modulului.
 *  Un fel necunoscut primește cota unei prize: e alegerea care greșește cel mai
 *  puțin, nu o afirmație. */
export function inaltimeaAparatului(kind: string, nivel: number): number {
  const a = PE_KIND.get(kind);
  if (!a) return 0.3;
  return a.h === "placa" ? nivel : a.h;
}

/** The size a kind's symbol is drawn at, before the pixel floor. */
export const marimeaSimbolului = (kind: string): number => {
  const a = PE_KIND.get(kind);
  return a?.simbol ?? a?.d ?? 0.16;
};

export const circuitulAparatului = (kind: string): Circuit =>
  PE_KIND.get(kind)?.circuit ?? "prize";

/** The two families a mounting box holds, by catalogue group. */
export type FamilieDoza = "prize" | "comenzi";
const FAMILIA_GRUPEI: Record<string, FamilieDoza> = { Prize: "prize", Comenzi: "comenzi" };

/** The family a kind belongs to in a box, or null for one a box cannot hold
 *  (lights, the board, another box). */
export const familiaInDoza = (kind: string): FamilieDoza | null =>
  FAMILIA_GRUPEI[PE_KIND.get(kind)?.grupa ?? ""] ?? PE_KIND.get(kind)?.inDoza ?? null;

/** The modules a box's devices occupy, per family: the `<n>M` drawn in it. */
export function moduleleDozei(devices: readonly string[]): Record<FamilieDoza, number> {
  const out: Record<FamilieDoza, number> = { prize: 0, comenzi: 0 };
  for (const k of devices) {
    const f = familiaInDoza(k);
    if (f) out[f] += PE_KIND.get(k)?.module ?? 1;
  }
  return out;
}

/** The families present in a box, sockets first: the order its halves and
 *  rows are drawn in. */
export function familiileDozei(devices: readonly string[]): FamilieDoza[] {
  const n = moduleleDozei(devices);
  return (["prize", "comenzi"] as const).filter((f) => n[f] > 0);
}

/** The frame a box needs: its devices' modules rounded up to an even count,
 *  since frames come in 2, 4, 6… and a free module takes a blank cover. */
export function ramaDozei(devices: readonly string[]): number {
  const n = moduleleDozei(devices);
  return Math.ceil((n.prize + n.comenzi) / 2) * 2;
}

/** The family a box is drawn and chained as: sockets if it holds any, so a
 *  mixed box reads as a socket box; null when empty. */
export function familiaDozei(devices: readonly string[]): FamilieDoza | null {
  const n = moduleleDozei(devices);
  return n.prize > 0 ? "prize" : n.comenzi > 0 ? "comenzi" : null;
}

/** Whether a kind stands on a wall, and so measures along it: a box, the LED
 *  strip, and anything not hung from the slab. An unknown kind counts as on a
 *  wall, where almost every device is. */
export function ePePerete(kind: string): boolean {
  if (kind === "doza" || kind === "banda_led") return true;
  return PE_KIND.get(kind)?.h !== "placa";
}

/** The kinds that carry a switch index (sheet row 119): sockets, switches and
 *  lights, but not the master switch, which commands the whole dwelling. The
 *  TV and data outlets keep theirs among the weak currents. */
export const areIndice = (kind: string): boolean => kind !== "intrerupator_general"
  && (familiaInDoza(kind) != null || PE_KIND.get(kind)?.grupa === "Iluminat");

/** A box's default height: the highest of its devices', since one frame holds
 *  them all and the higher device decides where it goes (a worktop socket
 *  over a skirting one). An empty box takes a socket's. */
export function inaltimeaDozei(devices: readonly string[], nivel: number): number {
  if (!devices.length) return inaltimeaAparatului("priza", nivel);
  return Math.max(...devices.map((k) => inaltimeaAparatului(k, nivel)));
}

/** A mounting box in the 3D view, one frame in the wall: 3.5 cm off the face,
 *  the catalogue's length along the wall, a frame's 10 cm tall. */
export const dimensiuniDozei = (): [number, number, number] =>
  [0.035, PE_KIND.get("doza")!.d, 0.1];

/** The distinct circuits of a box's devices, in the order they first appear:
 *  one wire route each, so a TV socket in a box stays on the data circuit. */
export function circuiteleDozei(devices: readonly string[]): Circuit[] {
  return [...new Set(devices.map(circuitulAparatului))];
}

/** Aparatele care nu se leagă la niciun traseu, fiindcă ele SUNT capătul lui. */
export const esteSursa = (kind: string): boolean =>
  PE_KIND.get(kind)?.rol === "sursa";

/** Rețeaua felului; unul necunoscut e electric, ca până la instalații. */
export const reteaAparatului = (kind: string): Retea => PE_KIND.get(kind)?.retea ?? "electric";

// ------------------------------------------------------------------ traseul ---

/** Un segment de perete, ca ax cu două capete. Exact ce scrie `cuAxeScrise`. */
export type AxZid = { a: Pt; b: Pt };

/** TRASEUL UNUI FIR, în trei bucăți — v. nota din capul fișierului. */
export type Traseu = {
  /** Aparatul, ca poziție în `plan.electrical`. */
  idx: number;
  circuit: Circuit;
  /** Drumul orizontal, pe banda de pozare: de la aparat la tablou, pe axele
   *  pereților. Primul punct e sub/peste aparat, ultimul sub/peste tablou. */
  drum: Pt[];
  /** Cota la care merge drumul orizontal. */
  cota: number;
  /** Înălțimea aparatului și a tabloului — capetele celor două verticale. */
  laAparat: number;
  laTablou: number;
  /** Lungimea desfășurată, cu verticalele: cifra care se pune pe extras. */
  lungime: number;
  /** The point the wire feeds: the device's own, or a strip's nearer end. */
  la: Pt;
};

const dist = (p: Pt, q: Pt) => Math.hypot(q[0] - p[0], q[1] - p[1]);

/** Punctul de pe segmentul [a, b] cel mai apropiat de p, și cât de departe e. */
function peSegment(p: Pt, a: Pt, b: Pt): { q: Pt; t: number; d: number } {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const L2 = vx * vx + vy * vy;
  const t = L2 < 1e-12 ? 0
    : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
  const q: Pt = [a[0] + vx * t, a[1] + vy * t];
  return { q, t, d: dist(p, q) };
}

/** Cât de aproape trebuie să fie două capete ca să fie același nod.
 *
 *  Aceeași cifră ca `model::LIPIRE_NOD`, și din același motiv: axele sunt
 *  scrise din poligoane transcrise dintr-o planșă, iar două ziduri care se
 *  ating acolo au capetele la câțiva centimetri unul de altul. Sub pragul ăsta
 *  sunt un colț; peste el, două ziduri care nu se ating — iar un fir n-are cum
 *  să treacă dintr-unul în altul. */
const LIPIRE = 0.25;

/** Graful pe care umblă firele: nodurile pereților, plus punctele în care
 *  aparatele se agață de zidul lor. */
type Graf = {
  p: Pt[];
  /** Vecinii fiecărui nod, cu lungimea muchiei. */
  vecini: { la: number; d: number }[][];
};

/** Nodul din `g` care stă în `p`, sau unul nou. */
function nodul(g: Graf, p: Pt): number {
  for (let i = 0; i < g.p.length; i++) {
    if (dist(g.p[i], p) <= LIPIRE) return i;
  }
  g.p.push(p);
  g.vecini.push([]);
  return g.p.length - 1;
}

function leaga(g: Graf, i: number, j: number) {
  if (i === j) return;
  const d = dist(g.p[i], g.p[j]);
  if (g.vecini[i].some((v) => v.la === j)) return;
  g.vecini[i].push({ la: j, d });
  g.vecini[j].push({ la: i, d });
}

/** CÂT DE MARE E GOLUL PESTE CARE FIRUL TRECE OTICUM.
 *
 *  ZIDURILE CATALOGULUI SUNT TĂIATE LA FIECARE GOL. Așa au fost măsurate ușile
 *  și ferestrele — planșa taie stratul ZIDURI chiar în dreptul lor
 *  (`origin: "cut"`, v. nota din `editor/camere.ts`) —, deci un perete văzut ca
 *  o linie continuă e în document două-cinci bucăți cu câte o spărtură de un
 *  metru între ele.
 *
 *  Pentru un fir, spărtura aia NU e o întrerupere: traseul trece pe deasupra
 *  golului, peste buiandrug, cum trece orice instalație dintr-o cameră în alta.
 *  Fără puntea asta, graful se rupe la fiecare ușă și aproape niciun aparat nu
 *  mai ajunge la tablou — măsurat pe M2: 5 din 31.
 *
 *  2,40 m: mai mult decât cea mai lată fereastră a catalogului și mai puțin
 *  decât orice trecere liberă dintre două camere. Și numai ÎNTRE CAPETE
 *  COLINIARE: două ziduri paralele de o parte și de alta a unui coridor de doi
 *  metri n-au voie să se lege, fiindcă între ele chiar nu trece niciun fir.
 */
const PUNTE = 2.4;

/** GRAFUL PEREȚILOR, cu aparatele agățate de el.
 *
 *  Se face în trei pași, și toți trei sunt necesari:
 *
 *    1. SE PLANARIZEAZĂ. Capătul unui zid cade de obicei pe MIJLOCUL altuia —
 *       o îmbinare în T —, iar un graf făcut doar din capete n-ar avea acolo
 *       niciun nod comun: cele două ziduri s-ar atinge pe desen și ar fi
 *       străine în graf. Deci fiecare capăt se proiectează pe zidurile de lângă
 *       și le taie.
 *    2. SE AGAȚĂ APARATELE, la fel: proiecția fiecăruia taie zidul lui în două,
 *       exact ca doza scoasă la mijlocul unui perete. Un aparat prea departe de
 *       orice zid — o plafonieră, care CHIAR stă în mijlocul camerei — se leagă
 *       tot de cel mai apropiat punct de pe cel mai apropiat ax: firul ei
 *       traversează tavanul, ceea ce e adevărat.
 *    3. SE PUN PUNȚILE peste golurile din ziduri — v. `PUNTE`.
 */
function graful(zid: readonly AxZid[], puncte: readonly Pt[]):
    { g: Graf; alePunctelor: number[] } {
  const g: Graf = { p: [], vecini: [] };
  // Fiecare zid e o listă de puncte pe axul lui: capetele, plus tăieturile.
  // Sortate pe parametru, dau muchiile.
  const peZid: { t: number; p: Pt }[][] = zid.map((z) => [
    { t: 0, p: z.a }, { t: 1, p: z.b },
  ]);

  /** Taie zidul cel mai apropiat de `p`, dacă e destul de aproape. Întoarce
   *  punctul de pe ax — sau `p` însuși, când nu e niciun zid prin preajmă. */
  const taie = (p: Pt, raza: number): Pt => {
    let cel = -1;
    let cea = raza;
    let unde: { q: Pt; t: number } | null = null;
    zid.forEach((z, i) => {
      const r = peSegment(p, z.a, z.b);
      if (r.d < cea) { cea = r.d; cel = i; unde = { q: r.q, t: r.t }; }
    });
    if (cel < 0 || !unde) return p;
    const u = unde as { q: Pt; t: number };
    peZid[cel].push({ t: u.t, p: u.q });
    return u.q;
  };

  // 1. ÎMBINĂRILE ÎN T. Fiecare capăt taie zidurile pe care cade — dar nu pe
  //    ale lui, unde ar cădea chiar pe capăt și n-ar tăia nimic.
  for (const z of zid) {
    for (const capat of [z.a, z.b]) {
      zid.forEach((alt, i) => {
        if (alt === z) return;
        const r = peSegment(capat, alt.a, alt.b);
        if (r.d > LIPIRE) return;
        peZid[i].push({ t: r.t, p: r.q });
      });
    }
  }

  // 2. APARATELE. Fără rază: un aparat se leagă de zidul lui oricât ar fi de
  //    departe, fiindcă alternativa e să rămână nelegat.
  const proiectii = puncte.map((p) => taie(p, Infinity));

  // Muchiile: fiecare zid, tăiat la punctele lui.
  peZid.forEach((lista) => {
    lista.sort((a, b) => a.t - b.t);
    for (let i = 0; i + 1 < lista.length; i++) {
      leaga(g, nodul(g, lista[i].p), nodul(g, lista[i + 1].p));
    }
  });

  // 3. PUNȚILE PESTE GOLURI. Capetele libere ale zidurilor coliniare se leagă
  //    peste spărtura dintre ele — v. `PUNTE`.
  const capete = zid.flatMap((z) => {
    const L = Math.hypot(z.b[0] - z.a[0], z.b[1] - z.a[1]) || 1;
    const u: Pt = [(z.b[0] - z.a[0]) / L, (z.b[1] - z.a[1]) / L];
    return [{ p: z.a, u }, { p: z.b, u }];
  });
  for (let i = 0; i < capete.length; i++) {
    for (let j = i + 1; j < capete.length; j++) {
      const a = capete[i];
      const b = capete[j];
      const d = dist(a.p, b.p);
      if (d <= LIPIRE || d > PUNTE) continue;
      // Paralele? Produsul scalar al versorilor, în valoare absolută.
      if (Math.abs(a.u[0] * b.u[0] + a.u[1] * b.u[1]) < 0.985) continue;
      // Și golul dintre ele e PE lungul lor, nu de-a curmezișul: altfel două
      // ziduri paralele de o parte și de alta a unui coridor s-ar lega.
      const gx = (b.p[0] - a.p[0]) / d;
      const gy = (b.p[1] - a.p[1]) / d;
      if (Math.abs(gx * a.u[0] + gy * a.u[1]) < 0.985) continue;
      leaga(g, nodul(g, a.p), nodul(g, b.p));
    }
  }

  const alePunctelor = proiectii.map((q) => nodul(g, q));
  return { g, alePunctelor };
}

/** Dijkstra de la un nod: distanța și părintele fiecărui nod atins. */
function dijkstra(g: Graf, sursa: number): { d: number[]; din: number[] } {
  const n = g.p.length;
  const d = new Array<number>(n).fill(Infinity);
  const din = new Array<number>(n).fill(-1);
  const gata = new Array<boolean>(n).fill(false);
  d[sursa] = 0;
  // Coadă liniară: un modul are sub 200 de noduri, iar un heap aici ar fi mai
  // mult cod decât timp câștigat.
  for (let pas = 0; pas < n; pas++) {
    let u = -1;
    let cel = Infinity;
    for (let i = 0; i < n; i++) if (!gata[i] && d[i] < cel) { cel = d[i]; u = i; }
    if (u < 0) break;
    gata[u] = true;
    for (const v of g.vecini[u]) {
      if (d[u] + v.d < d[v.la]) { d[v.la] = d[u] + v.d; din[v.la] = u; }
    }
  }
  return { d, din };
}

/** TRASEELE, de la fiecare aparat la tabloul lui.
 *
 *  Tabloul: cel mai apropiat dintre cele scrise. Un tronson are un tablou de
 *  fiecare apartament (v. `auto`), iar un aparat aparține apartamentului din
 *  care se ajunge la el mai repede PE ZIDURI — nu în linie dreaptă. Diferența
 *  e chiar cazul obișnuit: două camere lipite spate în spate sunt la 30 cm una
 *  de alta și la douăzeci de metri pe traseu.
 *
 *  Un aparat de la care nu se poate ajunge la niciun tablou (zidurile lui nu se
 *  ating de restul) rămâne FĂRĂ traseu — nu primește unul inventat. Desenul îl
 *  arată atunci nelegat, ceea ce e informația corectă: acolo chiar nu ajunge
 *  niciun fir.
 */
export function trasee(
  aparate: readonly { idx: number; kind: string; c: Pt; h: number; circuit: string;
                      circuite?: readonly string[]; end?: Pt }[],
  ziduri: readonly AxZid[],
  nivel: number,
): Traseu[] {
  const tablouri = aparate.filter((a) => esteSursa(a.kind));
  if (!tablouri.length || !ziduri.length) return [];
  // A mounting box (`circuite` written by `deriva`) feeds one route per
  // circuit of its devices — the wires the electrician pulls to one frame;
  // empty, it draws no current and gets none.
  // numai aparatele electrice primesc fir: caloriferul și gura de aer le leagă
  // motorul de instalații
  const consumatori = aparate.flatMap((a) =>
    (esteSursa(a.kind) || reteaAparatului(a.kind) !== "electric" ? []
      : a.circuite ? a.circuite.map((c) => ({ ...a, circuit: c }))
        : [a]));
  if (!consumatori.length) return [];

  // A strip can be fed at either end: both go into the graph, and the one
  // with the shorter path wins.
  const capete = consumatori.map((a) => (a.end ? [a.c, a.end] : [a.c]));
  const puncte = [...tablouri.map((t) => t.c), ...capete.flat()];
  const { g, alePunctelor } = graful(ziduri, puncte);
  const nodTablou = alePunctelor.slice(0, tablouri.length);
  let k0 = tablouri.length;
  const nodAparat = capete.map((c) => {
    const noduri = alePunctelor.slice(k0, k0 + c.length);
    k0 += c.length;
    return noduri;
  });

  const deLaTablou = nodTablou.map((s) => dijkstra(g, s));

  const out: Traseu[] = [];
  consumatori.forEach((a, i) => {
    let tinta = nodAparat[i][0];
    let la = capete[i][0];
    let cel = -1;
    let cea = Infinity;
    deLaTablou.forEach((r, t) => {
      nodAparat[i].forEach((n, j) => {
        if (r.d[n] < cea) { cea = r.d[n]; cel = t; tinta = n; la = capete[i][j]; }
      });
    });
    if (cel < 0 || !Number.isFinite(cea)) return;   // nelegat: v. nota de sus
    // Drumul, refăcut înapoi de la aparat la tablou și întors: se citește
    // dinspre tablou, ca instalația.
    const drum: Pt[] = [];
    for (let u = tinta; u >= 0; u = deLaTablou[cel].din[u]) drum.push(g.p[u]);
    drum.reverse();
    const circuit = (a.circuit as Circuit) in CIRCUITE
      ? (a.circuit as Circuit) : "prize";
    const cota = cotaBenzii(circuit, nivel);
    let orizontal = 0;
    for (let k = 0; k + 1 < drum.length; k++) orizontal += dist(drum[k], drum[k + 1]);
    out.push({
      idx: a.idx,
      circuit,
      drum,
      cota,
      laAparat: a.h,
      laTablou: tablouri[cel].h,
      lungime: orizontal + Math.abs(cota - a.h) + Math.abs(cota - tablouri[cel].h),
      la,
    });
  });
  return out;
}

/** Cât fir intră pe fiecare circuit — cifra de pe extras.
 *
 *  DESFĂȘURATĂ, nu în plan: verticalele se numără. Un traseu de priză are un
 *  metru de urcat și de coborât la fiecare capăt, iar pe patruzeci de aparate
 *  asta e diferența dintre o estimare și o glumă. */
export function lungimiPeCircuit(t: readonly Traseu[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const x of t) out[x.circuit] = (out[x.circuit] ?? 0) + x.lungime;
  return out;
}
